// Issuer (peer) authorisation on an XRPL trustline — the THREE-state axis that
// `peerAuthorized` / `trustlinePeerAuthorized` carries on the wire.
//
// A fourth axis alongside the three already in the wallets module
// (CRYPTO_WALLET_STATUS / TRUSTLINE_STATUS / VASP_USER_STATUS) and orthogonal to
// all of them: a trustline can be `active` on the ledger and still not be
// authorised by the issuer, which is precisely the state that leaves an order
// parked at `processing_opt_in`.
//
// It lives in `shared/` rather than in the wallets module because the orders
// module reads the same flag under a different field name
// (`trustlinePeerAuthorized`) and the two surfaces must not word it differently.
//
// ── The rule this file exists to enforce ────────────────────────────────────
//
//   null   → the ledger has NEVER been read. Not a failure, not a refusal —
//            nothing is known. Renders neutral, and the operator's next move is
//            to read the ledger ("Check status").
//   false  → the ledger WAS read and the issuer has not authorised the line.
//            A wait on Ctrl-Alt, so it renders as a wait (warning), never as a
//            failure.
//   true   → the issuer has authorised. The line can receive the token.
//
// `null` and `false` must never be collapsed. Rendering `null` as `false` tells
// an operator the issuer has refused when in fact nobody has looked, and
// rendering it with a danger tint invents a failure that has not happened. No
// state on this axis is ever `danger`; a genuine trustline failure is the
// TRUSTLINE_STATUS axis's job (`failed`, with its own `failureReason`).

/** The resolved state — the discriminator every label / colour lookup keys off. */
export const PEER_AUTHORIZATION = {
  AUTHORIZED: 'authorized',
  AWAITING_ISSUER: 'awaiting_issuer',
  NOT_CHECKED: 'not_checked',
} as const

export type PeerAuthorizationState = (typeof PEER_AUTHORIZATION)[keyof typeof PEER_AUTHORIZATION]

// Values match StatusBadge's `color` prop. Deliberately no `danger` member —
// see the header: neither `false` nor `null` is a failure on this axis, and
// typing the return this narrowly makes "render null as a failure" a type error
// rather than a judgement call.
export type PeerAuthorizationColor = 'success' | 'warning' | 'neutral'

/**
 * Collapses the wire value to its state. `undefined` is treated as `null`:
 * a field the server did not send is as unknown as one it sent as null.
 */
export function peerAuthorizationState(
  peerAuthorized: boolean | null | undefined,
): PeerAuthorizationState {
  if (peerAuthorized === true) return PEER_AUTHORIZATION.AUTHORIZED
  if (peerAuthorized === false) return PEER_AUTHORIZATION.AWAITING_ISSUER
  return PEER_AUTHORIZATION.NOT_CHECKED
}

// OCP lookups keyed by the resolved state (coding-principles §9.4).
const PEER_AUTHORIZATION_LABEL: Record<PeerAuthorizationState, string> = {
  [PEER_AUTHORIZATION.AUTHORIZED]: 'Authorised by issuer',
  [PEER_AUTHORIZATION.AWAITING_ISSUER]: 'Awaiting issuer',
  [PEER_AUTHORIZATION.NOT_CHECKED]: 'Not checked',
}

const PEER_AUTHORIZATION_COLOR: Record<PeerAuthorizationState, PeerAuthorizationColor> = {
  [PEER_AUTHORIZATION.AUTHORIZED]: 'success',
  // A wait, not a refusal — Ctrl-Alt authorises on its own schedule.
  [PEER_AUTHORIZATION.AWAITING_ISSUER]: 'warning',
  // Nothing is known, so nothing is claimed.
  [PEER_AUTHORIZATION.NOT_CHECKED]: 'neutral',
}

// One sentence per state, in the operator's terms: what is true, and what moves
// it. The `not_checked` line names the action rather than implying a problem.
const PEER_AUTHORIZATION_DETAIL: Record<PeerAuthorizationState, string> = {
  [PEER_AUTHORIZATION.AUTHORIZED]:
    'The issuer has authorised this trustline, so the token can be delivered to it.',
  [PEER_AUTHORIZATION.AWAITING_ISSUER]:
    'The ledger holds this trustline but the issuer has not authorised it yet. Ctrl-Alt does that on its own schedule.',
  [PEER_AUTHORIZATION.NOT_CHECKED]:
    'The ledger has not been read for this trustline yet, so whether the issuer has authorised it is unknown. Check status to read it.',
}

export function peerAuthorizationLabel(peerAuthorized: boolean | null | undefined): string {
  return PEER_AUTHORIZATION_LABEL[peerAuthorizationState(peerAuthorized)]
}

export function peerAuthorizationColor(
  peerAuthorized: boolean | null | undefined,
): PeerAuthorizationColor {
  return PEER_AUTHORIZATION_COLOR[peerAuthorizationState(peerAuthorized)]
}

export function peerAuthorizationDetail(peerAuthorized: boolean | null | undefined): string {
  return PEER_AUTHORIZATION_DETAIL[peerAuthorizationState(peerAuthorized)]
}

/**
 * True only when the ledger has been read AND the issuer has not authorised —
 * i.e. the one state where "waiting on Ctrl-Alt" is a fact rather than a guess.
 * `null` returns false: an unread ledger is not evidence of a wait.
 */
export function isAwaitingIssuerAuthorization(peerAuthorized: boolean | null | undefined): boolean {
  return peerAuthorizationState(peerAuthorized) === PEER_AUTHORIZATION.AWAITING_ISSUER
}

/** True only when the ledger has never been read — the "Check status" case. */
export function isAuthorizationUnchecked(peerAuthorized: boolean | null | undefined): boolean {
  return peerAuthorizationState(peerAuthorized) === PEER_AUTHORIZATION.NOT_CHECKED
}
